import restGate from "./RestGate";
import { notify } from "./components/common/Toast/toastBridge";


// ─────────────────────────────────────────────────────────────
// IndexedDB cache for constants
// ─────────────────────────────────────────────────────────────

class DB {
  static DB_NAME = "ALGAPIA";
  static VERSION = 1; // version is never incremented; the DB is rather cleared

  private storeName: string;

  constructor(storeName: string) {
    this.storeName = storeName;
  }

  // private method to open DB
  private open(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB.DB_NAME, DB.VERSION);
      request.onerror = () => reject(request.error);
      request.onsuccess = (e) => resolve((e.target as IDBOpenDBRequest).result);
    });
  }

  static async create(tableList: string[]): Promise<void> {
    const db = await new Promise<IDBDatabase>((resolve, reject) => {
      const request = indexedDB.open(DB.DB_NAME, DB.VERSION);

      request.onupgradeneeded = (e) => {
        const db = (e.target as IDBOpenDBRequest).result;
        for (const s_n of tableList) {
          if (!db.objectStoreNames.contains(s_n)) {
            db.createObjectStore(s_n, { keyPath: "id" });
          }
        }
      };

      request.onerror = () => reject(request.error);
      request.onsuccess = (e) => resolve((e.target as IDBOpenDBRequest).result);
    });
    db.close();
  }

  static async delete(): Promise<void> {
    // deletes whole database
    await new Promise<void>((resolve, reject) => {
      const deleteReq = indexedDB.deleteDatabase(DB.DB_NAME);
      deleteReq.onsuccess = () => resolve();
      deleteReq.onerror = () => reject(deleteReq.error);
    });
    DB.VERSION = 1;
  }

  async saveAll(objList: unknown[]): Promise<number> {
    const db = await this.open();
    const result = await new Promise<number>((resolve, reject) => {
      const transaction = db.transaction(this.storeName, "readwrite");
      const store = transaction.objectStore(this.storeName);

      for (const obj of objList) {
        store.put(obj);
      }

      transaction.oncomplete = () => resolve(1);
      transaction.onerror = () => reject(transaction.error);
    });
    db.close();
    return result;
  }

  async getAll<T = any>(): Promise<T[]> {
    const db = await this.open();
    const result = await new Promise<T[]>((resolve, reject) => {
      const transaction = db.transaction(this.storeName, "readonly");
      const store = transaction.objectStore(this.storeName);
      const req = store.getAll();
      req.onsuccess = () => resolve(req.result as T[]);
      req.onerror = () => reject(req.error);
    });
    db.close();
    return result;
  }

  async deleteAll(): Promise<number> {
    const db = await this.open();
    const result = await new Promise<number>((resolve, reject) => {
      const transaction = db.transaction(this.storeName, "readwrite");
      const store = transaction.objectStore(this.storeName);
      store.clear();
      transaction.oncomplete = () => resolve(1);
      transaction.onerror = () => reject(transaction.error);
    });
    db.close();
    return result;
  }
}

// ─────────────────────────────────────────────────────────────
// Base helper
// ─────────────────────────────────────────────────────────────

class Helper {
  id?: unknown;

  equals(other: any): boolean {
    return other?.constructor === this.constructor && (this as any).id === other.id;
  }

  toJson(): Record<string, unknown> {
    return { ...this } as Record<string, unknown>;
  }

  static fromJson<T>(this: new (json: any) => T, json: any): T {
    return new this(json);
  }
}

// ─────────────────────────────────────────────────────────────
// User
// ─────────────────────────────────────────────────────────────

export interface UserData {
  admin?: boolean;
  statistician?: boolean;
  creation_timestamp?: number;
  tier?: any;
  email?: string;
  generation_id?: number;
  id: number | string;
  name?: string;
  picture_link?: string;
  referral_code?: string;
  referral_revenue?: number | string;
  bonus_revenue?: number | string;
  potential_bonus_revenue?: number | string;
  balance?: number | string;
  paid_out?: number | string;
  bank_account?: string | null;
  auto_withdraw?: boolean;
  active_order?: any;
  provider_id?: string;
  bonus_history_len?: number | string;
  full_name?: string;
  used_free_trial?: boolean;
  professional?: boolean;
  constants_version?: number | string;
  journey_done_step_ids: number[] | null;
  notice: string | null;
  pending_paid_out: number;
}

class User extends Helper {
  id: number;
  admin?: boolean;
  statistician?: boolean;
  creation_timestamp?: number;
  email?: string;
  generation_id?: number;
  name?: string;
  full_name?: string;
  picture_link?: string;
  referral_code?: string;
  tier?: any;
  referral_revenue: number;
  bonus_revenue: number;
  potential_bonus_revenue: number;
  balance: number;
  paid_out: number;
  bank_account?: string | null;
  auto_withdraw?: boolean;
  active_order?: any;
  provider_id?: string;
  bonus_history_len: number;
  used_free_trial?: boolean;
  professional?: boolean;
  constants_version?: number | string;
  journey_done_step_ids: number[] | null;
  notice: string | null;
  pending_paid_out: number;

  constructor({ admin, statistician, creation_timestamp, tier, email, generation_id, id, name, picture_link, referral_code, referral_revenue, bonus_revenue, potential_bonus_revenue, balance, paid_out, bank_account, auto_withdraw, active_order, provider_id, bonus_history_len, full_name, used_free_trial, professional, constants_version, journey_done_step_ids, notice, pending_paid_out }: UserData) {
    super();
    this.id = Number(id);
    this.admin = admin;
    this.statistician = statistician;
    this.creation_timestamp = creation_timestamp;
    this.email = email;
    this.generation_id = generation_id;
    this.name = name;
    this.full_name = full_name;
    this.picture_link = picture_link;
    this.referral_code = referral_code;
    this.tier = tier;
    this.referral_revenue = Number(referral_revenue);
    this.bonus_revenue = Number(bonus_revenue);
    this.potential_bonus_revenue = Number(potential_bonus_revenue);
    this.balance = Number(balance);
    this.paid_out = Number(paid_out);
    this.bank_account = bank_account;
    this.auto_withdraw = auto_withdraw;
    this.active_order = active_order;
    this.provider_id = provider_id;
    this.bonus_history_len = Number(bonus_history_len);
    this.used_free_trial = used_free_trial;
    this.professional = professional;
    this.constants_version = constants_version;
    this.journey_done_step_ids = journey_done_step_ids;
    this.notice = notice;
    this.pending_paid_out = pending_paid_out;
  }

  static async create({ referralCode, name, firstName, lastName, birthTimestamp }: { referralCode?: string; name?: string; firstName?: string; lastName?: string; birthTimestamp?: number }): Promise<any> {
    return restGate.sendRequest({
      method: "POST",
      endpoint: "/user",
      body: {
        name,
        first_name: firstName,
        last_name: lastName,
        birth_timestamp: birthTimestamp,
        referral_code: referralCode,
      },
    });
  }

  static async get(): Promise<User> {
    const response = await restGate.sendRequest<UserData>({
      method: "GET",
      endpoint: "/user",
    });
    (response as any).active_order = new Order((response as any).active_order);
    return new User(response);
  }

  async update(): Promise<any> {
    return restGate.sendRequest({
      method: "PUT",
      endpoint: "/user",
      body: {
        name: this.name,
        bank_account: this.bank_account,
        email: this.email,
        auto_withdraw: this.auto_withdraw,
        professional: this.professional,
        journey_done_step_ids: this.journey_done_step_ids,
        notice: this.notice
      },
    });
  }

  async delete(): Promise<any> {
    return restGate.sendRequest({ method: "DELETE", endpoint: "/user" });
  }

  static async deleteFirebaseUser(): Promise<any> {
    return restGate.sendRequest({ method: "DELETE", endpoint: "/user/firebase_deletion" });
  }

  static async passwordForgotten({ email }: { email: string }): Promise<any> {
    return restGate.sendRequest({
      method: "GET",
      endpoint: "/user/password_forgotten",
      params: { email },
      auth: false,
    });
  }

  static async getStats(): Promise<any> {
    return restGate.sendRequest({
      method: "GET",
      endpoint: "/user/get_stats",
      auth: false,
    });
  }
}

// ─────────────────────────────────────────────────────────────
// Order
// ─────────────────────────────────────────────────────────────

export interface OrderData {
  amount?: number;
  creation_timestamp?: number;
  expiration_timestamp?: number;
  id?: number | string;
  status_id?: number;
  tier_id?: number;
  user_id?: number;
  last_paid_amount?: number;
  redirection_url?: string;
}

class Order extends Helper {
  amount?: number;
  creation_timestamp?: number;
  expiration_timestamp?: number;
  id?: number | string;
  status_id?: number;
  tier_id?: number;
  user_id?: number;
  last_paid_amount?: number;
  redirection_url?: string;

  constructor({ amount, creation_timestamp, expiration_timestamp, id, status_id, tier_id, user_id, last_paid_amount, redirection_url }: OrderData = {}) {
    super();
    this.amount = amount;
    this.creation_timestamp = creation_timestamp;
    this.expiration_timestamp = expiration_timestamp;
    this.id = id;
    this.status_id = status_id;
    this.tier_id = tier_id;
    this.user_id = user_id;
    this.last_paid_amount = last_paid_amount;
    this.redirection_url = redirection_url;
  }

  static async get(id: number | string): Promise<Order> {
    const response = await restGate.sendRequest<OrderData>({
      method: "GET",
      endpoint: "/order",
      params: { id },
    });
    return new Order(response);
  }

  static async create({ tierId, subscriptionScope }: { tierId: number; subscriptionScope: string }): Promise<Order> {
    const response = await restGate.sendRequest<OrderData>({
      method: "POST",
      endpoint: "/order",
      body: { tier_id: tierId, subscription_scope: subscriptionScope },
    });
    return new Order(response);
  }

  static async getStatus(): Promise<any> {
    return restGate.sendRequest({ method: "GET", endpoint: "/order-status" });
  }

  static async getPaywallUrl(): Promise<string> {
    const response = await restGate.sendRequest<{ paywall_url: string }>({
      method: "GET",
      endpoint: "/order-paywall-url",
    });
    return response.paywall_url;
  }

  static async getOrderManagingUrl(redirection_url: string): Promise<string> {
    const response = await restGate.sendRequest<{ managing_url: string }>({
      method: "GET",
      endpoint: "/order-managing",
      params: { redirection_url },
    });
    return response.managing_url;
  }

  static async manualCheck(): Promise<null> {
    await restGate.sendRequest({ method: "GET", endpoint: "/order-status-manual" });
    return null;
  }
}

// ─────────────────────────────────────────────────────────────
// Category
// ─────────────────────────────────────────────────────────────

class Category extends Helper {
  id?: number;
  name?: string;
  subtitle?: string;
  description?: string;
  pros_list?: string[];
  cons_list?: string[];

  constructor({ id, name, subtitle, description, pros_list, cons_list }: any) {
    super();
    this.id = id;
    this.name = name;
    this.subtitle = subtitle;
    this.description = description;
    this.pros_list = pros_list;
    this.cons_list = cons_list;
  }

  static async getAll(): Promise<Category[]> {
    const response = await restGate.sendRequest<any[]>({ method: "GET", endpoint: "/category_all" });
    return response.map((cat) => new Category(cat));
  }
}

// ─────────────────────────────────────────────────────────────
// Bonus
// ─────────────────────────────────────────────────────────────

class Bonus extends Helper {
  id?: number;
  creation_timestamp?: number;
  description?: string;
  end_timestamp?: number;
  expected_value?: number;
  games: Game[];
  name?: string;
  needed_capital?: number;
  special_mark?: any;
  start_timestamp?: number;
  step_titles?: any;
  step_list?: any;
  url_bonus_conditions?: string;
  url_link?: string;
  visibility_end_timestamp?: number;
  visibility_start_timestamp?: number;
  visible?: boolean;
  risk_rate?: number;
  company?: any;
  bonus_type_name?: string;
  step_idx?: number;
  start_balance?: number;
  renewing_days?: number;

  constructor({ creation_timestamp, description, end_timestamp, expected_value, games, id, name, needed_capital, special_mark, start_timestamp, step_titles, url_bonus_conditions, url_link, visibility_end_timestamp, visibility_start_timestamp, visible, risk_rate, company, bonus_type_name, step_idx, start_balance, renewing_days, step_list }: any) {
    super();
    this.id = id;
    this.creation_timestamp = creation_timestamp;
    this.description = description;
    this.end_timestamp = end_timestamp;
    this.expected_value = expected_value;
    this.games = (games as any[]).map((b) => new Game(b));
    this.name = name;
    this.needed_capital = needed_capital;
    this.special_mark = special_mark;
    this.start_timestamp = start_timestamp;
    this.step_titles = step_titles;
    this.step_list = step_list;
    this.url_bonus_conditions = url_bonus_conditions;
    this.url_link = url_link;
    this.visibility_end_timestamp = visibility_end_timestamp;
    this.visibility_start_timestamp = visibility_start_timestamp;
    this.visible = visible;
    this.risk_rate = risk_rate;
    this.company = company;
    this.bonus_type_name = bonus_type_name;
    this.step_idx = step_idx;
    this.start_balance = start_balance;
    this.renewing_days = renewing_days;
  }

  static async create({ games, company, ...rest }: any): Promise<any> {
    return restGate.sendRequest({
      method: "POST",
      endpoint: "/bonus",
      body: {
        ...rest,
        games_ids: games.map((g: any) => g.id),
        company_id: company.id,
      },
    });
  }

  static async getAll(): Promise<Bonus[]> {
    const response = await restGate.sendRequest<any[]>({ method: "GET", endpoint: "/bonus_all" });
    return response.map((bonus) => new Bonus(bonus));
  }

  static async getAllAdmin({ page, itemsPerPage, bonusTypeFilters, renewingFilter }: { page: number; itemsPerPage: number; bonusTypeFilters: any; renewingFilter: any }): Promise<Bonus[]> {
    const response = await restGate.sendRequest<any[]>({
      method: "POST",
      endpoint: "/bonus_all_admin",
      body: {
        current_page: page,
        items_per_page: itemsPerPage,
        bonus_type_filters: bonusTypeFilters,
        renewing_filter: renewingFilter,
      },
    });
    return response.map((bonus) => new Bonus(bonus));
  }

  static async get(id: number | string): Promise<Bonus> {
    const response = await restGate.sendRequest<any>({ method: "GET", endpoint: "/bonus", params: { id } });
    return new Bonus(response);
  }

  async update(): Promise<any> {
    const { games, company, step_list, ...rest } = this as any;
    return restGate.sendRequest({
      method: "PUT",
      endpoint: "/bonus",
      body: {
        ...rest,
        games_ids: games.map((g: any) => g.id),
        company_id: company.id,
        step_list,
      },
    });
  }

  async hide(): Promise<any> {
    return restGate.sendRequest({ method: "PUT", endpoint: "/bonus_hide", body: { id: this.id } });
  }

  async getSteps(): Promise<any> {
    const response = await restGate.sendRequest({ method: "GET", endpoint: "/bonus_steps", params: { id: this.id } });
    this.step_list = response;
    return response;
  }
}

// ─────────────────────────────────────────────────────────────
// CustomBonus
// ─────────────────────────────────────────────────────────────

class CustomBonus extends Helper {
  id?: number;
  name?: string;
  company?: any;
  bonus_type_name?: string;

  constructor({ id, name, company, bonus_type_name }: any) {
    super();
    this.id = id;
    this.name = name;
    this.company = company;
    this.bonus_type_name = bonus_type_name;
  }

  static async create({ company, ...rest }: any): Promise<any> {
    return restGate.sendRequest({
      method: "POST",
      endpoint: "/custom_bonus",
      body: { ...rest, company_id: company.id },
    });
  }
}

// ─────────────────────────────────────────────────────────────
// InvestmentCategory & Investment
// ─────────────────────────────────────────────────────────────

class InvestmentCategory extends Helper {
  id?: number;
  name?: string;
  subtitle?: string;
  description?: string;
  pros_list?: string[];
  cons_list?: string[];
  avg_expected_return?: number;
  avg_risk_rate?: number;

  constructor({ id, name, subtitle, description, pros_list, cons_list, avg_expected_return, avg_risk_rate }: any) {
    super();
    this.id = id;
    this.name = name;
    this.subtitle = subtitle;
    this.description = description;
    this.pros_list = pros_list;
    this.cons_list = cons_list;
    this.avg_expected_return = avg_expected_return;
    this.avg_risk_rate = avg_risk_rate;
  }

  static async getAll(): Promise<InvestmentCategory[]> {
    const response = await restGate.sendRequest<any[]>({ method: "GET", endpoint: "/investment_category_all" });
    return response.map((i_c) => new InvestmentCategory(i_c));
  }
}

class Investment extends Helper {
  id?: number;
  investment_category_id?: number;
  company_name?: string;
  cons_list?: string[];
  creation_timestamp?: number;
  description?: string;
  expected_return?: number;
  invest_horizont_id?: number;
  name?: string;
  needed_capital?: number;
  pros_list?: string[];
  risk_rate?: number;
  step_list?: any;
  url_link?: string;

  constructor({ investment_category_id, company_name, cons_list, creation_timestamp, description, expected_return, id, invest_horizont_id, name, needed_capital, pros_list, risk_rate, step_list, url_link }: any) {
    super();
    this.id = id;
    this.investment_category_id = investment_category_id;
    this.company_name = company_name;
    this.cons_list = cons_list;
    this.creation_timestamp = creation_timestamp;
    this.description = description;
    this.expected_return = expected_return;
    this.invest_horizont_id = invest_horizont_id;
    this.name = name;
    this.needed_capital = needed_capital;
    this.pros_list = pros_list;
    this.risk_rate = risk_rate;
    this.step_list = step_list;
    this.url_link = url_link;
  }

  static async getAll(investmentCategoryId: number | null = null): Promise<Investment[]> {
    const response = await restGate.sendRequest<any[]>({
      method: "GET",
      endpoint: "/investment_all",
      params: { investment_category_id: investmentCategoryId },
    });
    return response.map((investment) => new Investment(investment));
  }

  static async get(id: number | string): Promise<Investment> {
    const response = await restGate.sendRequest<any>({ method: "GET", endpoint: "/investment", params: { id } });
    return new Investment(response);
  }
}

// ─────────────────────────────────────────────────────────────
// Tier
// ─────────────────────────────────────────────────────────────

class Tier extends Helper {
  id?: number;
  name?: string;
  subtitle?: string;
  features?: any;
  featured?: boolean;
  color?: string;
  price: number;
  button_text?: string;
  max_return?: number;
  referral_reward?: number;
  special_mark?: any;
  in_use?: boolean;
  free_trial_days?: number;

  constructor({ id, name, subtitle, features, featured, color, price, button_text, max_return, referral_reward, special_mark, in_use, free_trial_days }: any) {
    super();
    this.id = id;
    this.name = name;
    this.subtitle = subtitle;
    this.features = features;
    this.featured = featured;
    this.color = color;
    this.price = Number(price);
    this.button_text = button_text;
    this.max_return = max_return;
    this.referral_reward = referral_reward;
    this.special_mark = special_mark;
    this.in_use = in_use;
    this.free_trial_days = free_trial_days;
  }

  static async getAll(): Promise<Tier[]> {
    const response = await restGate.sendRequest<any[]>({ method: "GET", endpoint: "/tier_all", auth: false });
    return response.map((tier) => new Tier(tier));
  }
}

// ─────────────────────────────────────────────────────────────
// PromoCode
// ─────────────────────────────────────────────────────────────

class PromoCodeObj extends Helper {
  id?: number;
  discount?: number;
  code?: string;
  months?: number;

  constructor({ id, code, months, discount }: any) {
    super();
    this.id = id;
    this.discount = discount;
    this.code = code;
    this.months = months;
  }

  static async checkValidity(promo_code: string, tier_id: number): Promise<PromoCodeObj> {
    const response = await restGate.sendRequest<any>({
      method: "GET",
      endpoint: "/promo_code/check_validity",
      params: { promo_code, tier_id },
    });
    return new PromoCodeObj(response);
  }
}

// ─────────────────────────────────────────────────────────────
// BonusHistory
// ─────────────────────────────────────────────────────────────

class BonusHistoryObj extends Helper {
  id?: number;
  bonus?: any;
  note?: string;
  reached_profit?: number;
  creation_timestamp?: number;

  constructor({ bonus, id, note, reached_profit, creation_timestamp }: any) {
    super();
    this.id = id;
    this.bonus = bonus;
    this.note = note;
    this.reached_profit = reached_profit;
    this.creation_timestamp = creation_timestamp;
  }

  static async create({ bonusId, note, amount }: { bonusId: number; note?: string; amount: number }): Promise<any> {
    return restGate.sendRequest({
      method: "POST",
      endpoint: "/bonus_history",
      body: { bonus_id: bonusId, note, reached_profit: amount },
    });
  }

  static async getAll(currentPage: number, itemsPerPage: number, selectedPeriod: any, activeTypeFilters: any): Promise<BonusHistoryObj[]> {
    const response = await restGate.sendRequest<any[]>({
      method: "POST",
      endpoint: "/bonus_history_all",
      body: {
        current_page: currentPage,
        items_per_page: itemsPerPage,
        time_period: selectedPeriod,
        bonus_type_filters: activeTypeFilters,
      },
    });
    return response.map((bh) => new BonusHistoryObj(bh));
  }

  async update(): Promise<any> {
    return restGate.sendRequest({
      method: "PUT",
      endpoint: "/bonus_history",
      body: { id: this.id, reached_profit: this.reached_profit },
    });
  }

  async delete(): Promise<any> {
    return restGate.sendRequest({ method: "DELETE", endpoint: "/bonus_history", params: { id: this.id } });
  }

  static async getStats(timePeriod: any): Promise<any> {
    return restGate.sendRequest({
      method: "GET",
      endpoint: "/bonus_history_stats",
      params: { time_period: timePeriod },
    });
  }
}

class BonusProgress extends Helper {
  static async updateStepIdx(bonusId: number, step_idx: number): Promise<any> {
    return restGate.sendRequest({
      method: "PUT",
      endpoint: "/bonus_progress",
      body: { bonus_id: bonusId, step_idx },
    });
  }

  static async updateStartBalance(bonusId: number, start_balance: number): Promise<any> {
    return restGate.sendRequest({
      method: "PUT",
      endpoint: "/bonus_progress",
      body: { bonus_id: bonusId, start_balance },
    });
  }
}

// ─────────────────────────────────────────────────────────────
// Referenced
// ─────────────────────────────────────────────────────────────

class Referenced extends Helper {
  name?: string;
  creation_timestamp?: number;
  tier_name?: string;
  referencer_reward?: number;

  constructor({ name, creation_timestamp, tier_name, referencer_reward }: any) {
    super();
    this.name = name;
    this.creation_timestamp = creation_timestamp;
    this.tier_name = tier_name;
    this.referencer_reward = referencer_reward;
  }

  static async getAll(): Promise<Referenced[]> {
    const response = await restGate.sendRequest<any[]>({ method: "GET", endpoint: "/referenced_all" });
    return response.map((r) => new Referenced(r));
  }

  static async sendRegEmail(email: string, referralCode: string): Promise<any> {
    return restGate.sendRequest({
      method: "POST",
      endpoint: "/referenced/send_reg_email",
      body: { email, referral_code: referralCode },
      auth: false,
    });
  }
}

// ─────────────────────────────────────────────────────────────
// Transaction
// ─────────────────────────────────────────────────────────────

interface TransactionData {
  id: number;
  user_id: number;
  user_name: string;
  amount: number;
  creation_timestamp: number;
  trans_status_id: number;
  trans_type_id: number;
  bank_account?: string;
}

interface CreateTransactionParams {
  amount: number;
  currentBankAccount: string;
}

class Transaction extends Helper {
  id: number;
  user_id: number;
  user_name: string;
  amount: number;
  creation_timestamp: number;
  trans_status_id: number;
  trans_type_id: number;
  bank_account?: string;

  constructor({ id, user_id, user_name, amount, creation_timestamp, bank_account, trans_status_id, trans_type_id }: TransactionData) {
    super();
    this.id = Number(id);
    this.user_id = Number(user_id);
    this.user_name = user_name;
    this.trans_status_id = Number(trans_status_id);
    this.trans_type_id = Number(trans_type_id);
    this.amount = Number(amount);
    this.creation_timestamp = Number(creation_timestamp);
    this.bank_account = bank_account;
  }

  static async create({ amount, currentBankAccount }: CreateTransactionParams): Promise<any> {
    return restGate.sendRequest({
      method: "POST",
      endpoint: "/transaction",
      body: { amount, bank_account: currentBankAccount },
    });
  }

  static async getStats(timePeriod: any): Promise<any> {
    return restGate.sendRequest({
      method: "GET",
      endpoint: "/transaction_stats",
      params: { time_period: timePeriod },
    });
  }

  // admin
  static async getAll(trans_type_id: number, trans_status_id: number, page_idx: number, page_size: number): Promise<Transaction[]> {
    const response = await restGate.sendRequest<any[]>({
      method: "GET",
      endpoint: "/transaction_all",
      params: {
        trans_type_id,
        trans_status_id,
        page_idx,
        page_size
      }
    });
    return response.map((transaction) => new Transaction(transaction));
  }

  async update(): Promise<any> {
    return restGate.sendRequest({
      method: "PUT",
      endpoint: "/transaction",
      body: {
        id: this.id,
        bank_account: this.bank_account,
        amount: this.amount,
        trans_status_id: this.trans_status_id,
        trans_type_id: this.trans_type_id,
      }
    });
  }

  async payOut(): Promise<any> {
    return restGate.sendRequest({
      method: "PATCH",
      endpoint: "/transaction/pay_out",
      params: {
        id: this.id,
      }
    });
  }
}

// ─────────────────────────────────────────────────────────────
// Generation
// ─────────────────────────────────────────────────────────────

class Generation extends Helper {
  id?: number;
  name?: string;
  limit?: number;
  current_load?: number;
  in_use?: boolean;

  constructor({ id, name, limit, current_load, in_use }: any) {
    super();
    this.id = id;
    this.name = name;
    this.limit = limit;
    this.current_load = current_load;
    this.in_use = in_use;
  }

  static async getCurrent(): Promise<Generation> {
    const response = await restGate.sendRequest<any>({ method: "GET", endpoint: "/generation", auth: false });
    return new Generation(response);
  }
}

// ─────────────────────────────────────────────────────────────
// ReferralCode
// ─────────────────────────────────────────────────────────────

class ReferralCode extends Helper {
  id: number;
  creation_timestamp: number;
  code: string;
  referencer_name: string;

  constructor({ id, creation_timestamp, code, referencer_name }: any) {
    super();
    this.id = id;
    this.creation_timestamp = creation_timestamp;
    this.code = code;
    this.referencer_name = referencer_name;
  }

  // deprecated
  static async create(): Promise<ReferralCode> {
    const response = await restGate.sendRequest<any>({ method: "POST", endpoint: "/referral_code" });
    return new ReferralCode(response);
  }

  static async getAll(): Promise<ReferralCode[]> {
    const response = await restGate.sendRequest<any[]>({ method: "GET", endpoint: "/referral_code_all" });
    return response.map((r_c) => new ReferralCode(r_c));
  }

  static async checkValidity(referralCode: string): Promise<ReferralCode> {
    const response = await restGate.sendRequest({
      method: "GET",
      endpoint: "/referral_code/check_validity",
      params: { referral_code: referralCode },
      auth: false,
    });

    return new ReferralCode(response);
  }
}

// ─────────────────────────────────────────────────────────────
// ScreeniaSession
// ─────────────────────────────────────────────────────────────

class ScreeniaSession extends Helper {
  id?: number;
  creation_timestamp?: number;
  code?: string;
  in_use?: boolean;

  constructor({ id, creation_timestamp, code, in_use }: any) {
    super();
    this.id = id;
    this.creation_timestamp = creation_timestamp;
    this.code = code;
    this.in_use = in_use;
  }

  static async create(): Promise<any> {
    return restGate.sendRequest({ method: "POST", endpoint: "/screenia" });
  }

  static async getAll(): Promise<any> {
    return restGate.sendRequest({ method: "GET", endpoint: "/screenia" });
  }
}

// ─────────────────────────────────────────────────────────────
// FAQ
// ─────────────────────────────────────────────────────────────

class FaqObj extends Helper {
  id?: number;
  answer?: string;
  question?: string;
  faq_category_name?: string;

  constructor({ id, answer, question, faq_category_name }: any) {
    super();
    this.id = id;
    this.answer = answer;
    this.question = question;
    this.faq_category_name = faq_category_name;
  }

  static async getAll(): Promise<FaqObj[]> {
    const response = await restGate.sendRequest<any[]>({ method: "GET", endpoint: "/faq_all" });
    return response.map((f) => new FaqObj(f));
  }
}

// ─────────────────────────────────────────────────────────────
// Statistics
// ─────────────────────────────────────────────────────────────

class Statistics extends Helper {
  constructor(_: any = {}) {
    super();
  }

  static async get(table_name: string): Promise<any> {
    return restGate.sendRequest({ method: "GET", endpoint: "/statistics", params: { table_name } });
  }
}

// ─────────────────────────────────────────────────────────────
// Constants (cached in IndexedDB)
// ─────────────────────────────────────────────────────────────

class Constant extends Helper {
  static dbName: string | null = null;
  static endpoint: string | null = null;
  static values: any[] = [];
  private static _db: DB | null = null; // backing private field for db property

  static get db(): DB {
    if (!this._db) {
      this._db = new DB(this.dbName as string);
    }
    return this._db;
  }

  static get(srchObj: Record<string, unknown> = {}): any | null {
    const filtered = this.values.filter((o) =>
      Object.entries(srchObj).every(([key, value]) => o[key] === value),
    );
    if (!filtered.length) return null;
    return filtered[0];
  }

  static async loadValues(force?: boolean): Promise<void> {
    let response: any[] = [];
    try {
      response = await this.db.getAll();
    } catch {
      response = [];
    }

    if (!response.length || force) {
      response = await restGate.sendRequest<any[]>({
        method: "GET",
        endpoint: this.endpoint as string,
      });
      await this.db.saveAll(response);
    }
    this.values = response.map((item) => new (this as any)(item));
  }
}

class Company extends Constant {
  static dbName = "Company";
  static endpoint = "/constants/company_all";
  id?: number;
  name?: string;

  constructor({ id, name }: any) {
    super();
    this.id = id;
    this.name = name;
  }
}

class BonusType extends Constant {
  static dbName = "BonusType";
  static endpoint = "/constants/bonus_type_all";
  id?: number;
  name?: string;
  label?: string;
  color?: string;
  background_color?: string;
  icon?: string;

  constructor({ id, name, label, color, background_color, icon }: any) {
    super();
    this.id = id;
    this.name = name;
    this.label = label;
    this.color = color;
    this.background_color = background_color;
    this.icon = icon;
  }
}

class Game extends Constant {
  static dbName = "Game";
  static endpoint = "/constants/game_all";
  id?: number;
  name?: string;
  rtp?: number;
  volatility?: number;
  game_studio_id?: number;
  url_link?: string;

  constructor({ id, name, rtp, volatility, game_studio_id, url_link }: any) {
    super();
    this.id = id;
    this.name = name;
    this.rtp = rtp;
    this.volatility = volatility;
    this.game_studio_id = game_studio_id;
    this.url_link = url_link;
  }
}

class Risk extends Constant {
  static dbName = "Risk";
  static endpoint = "/constants/risk_all";
  id?: number;
  rate?: number;
  label?: string;
  icon?: string;
  color?: string;
  color_not_clicked?: string;
  index?: number;

  constructor({ id, rate, label, icon, color, color_not_clicked, index }: any) {
    super();
    this.id = id;
    this.rate = rate;
    this.label = label;
    this.icon = icon;
    this.color = color;
    this.color_not_clicked = color_not_clicked;
    this.index = index;
  }
}

class SpecialMark extends Constant {
  static dbName = "SpecialMark";
  static endpoint = "/constants/special_mark_all";
  id?: number;
  name?: string;
  color?: string;
  label?: string;

  constructor({ id, name, color, label }: any) {
    super();
    this.id = id;
    this.name = name;
    this.color = color;
    this.label = label;
  }
}

class TransStatus extends Constant {
  static dbName = "TransStatus";
  static endpoint = "/constants/trans_status_all";
  id: number;
  name: string;

  constructor({ id, name }: any) {
    super();
    this.id = id;
    this.name = name;
  }
}

class TransType extends Constant {
  static dbName = "TransType";
  static endpoint = "/constants/trans_type_all";
  id: number;
  name: string;

  constructor({ id, name }: any) {
    super();
    this.id = id;
    this.name = name;
  }
}

/** Structural type covering the static members ConstantsManager relies on. */
type ConstantClass = {
  dbName: string | null;
  loadValues(force?: boolean): Promise<void>;
};

class ConstantsManager {
  static constants: ConstantClass[] = [Company, Game, BonusType, Risk, SpecialMark, TransStatus, TransType];

  static async loadAll(force = false): Promise<void> {
    await Promise.all(ConstantsManager.constants.map((c) => c.loadValues(force)));
    console.log("constants updated");
  }

  static async refreshCache(): Promise<void> {
    await DB.delete();
    await DB.create(ConstantsManager.constants.map((c) => c.dbName as string));
    console.log("cache refreshed");
  }

  static async onSessionStart(user: User): Promise<void> {
    // if the version does not exist on the browser, OR it differs from the newest version on backend, then recache them
    const constantsVersionLocal = localStorage.getItem("constants_version");
    let succeeded: boolean = false;

    if (constantsVersionLocal && constantsVersionLocal === String(user.constants_version)) {
      try {
        await ConstantsManager.loadAll(false);
        succeeded = true;
      } catch (error: any) {
        console.error(error);
      }
    }

    // if something failed, then load the fresh values from the backend
    if (!succeeded) {
      console.log("forced constants update...");
      await ConstantsManager.refreshCache(); // prepare cache for new constants
      await ConstantsManager.loadAll(true); // force load new constants from backend
      localStorage.setItem("constants_version", String(user.constants_version));
    }
  }

  static async onSessionEnd(): Promise<void> {
    try {
      await DB.delete();
      localStorage.removeItem("constants_version");
    } catch {
      /* ignore */
    }
  }
}

// ─────────────────────────────────────────────────────────────
// Push notifications
// ─────────────────────────────────────────────────────────────


export interface PushSubscription {
  user_id: number;
  user_name: string;
}

class PushNotificationManager extends Helper {
  static supported = Boolean(navigator.serviceWorker && window.PushManager && window.Notification);
  static pushNotificationsSet = (): boolean => !!localStorage.getItem("push_notifications_set");

  static urlBase64ToUint8Array(base64String: string): Uint8Array {
    const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
    const rawData = window.atob(base64);
    return Uint8Array.from([...rawData].map((c) => c.charCodeAt(0)));
  }

  static async onSessionStart(): Promise<void> {
    if (!this.supported) {
      console.log("Push notifications are not allowed in this browser");
      return;
    }
    // idempotential managing
    if (localStorage.getItem("push_notifications_set")) {
      await this.register();
    }
  }

  static async onSessionEnd(): Promise<void> {
    await this.unregister(false);
  }

  static async toggle(): Promise<boolean> {
    const pushSetted = this.pushNotificationsSet();
    if (!pushSetted) {
      await this.register();
    } else {
      await this.unregister();
    }
    return this.pushNotificationsSet();
  }

  static async register(remember = true): Promise<void> {
    try {
      if (!process.env.REACT_APP_VAPID_PUBLIC_KEY) {
        throw new Error("REACT_APP_VAPID_PUBLIC_KEY is null");
      }

      let permission = Notification.permission;
      if (permission !== "granted") {
        permission = await Notification.requestPermission();
        if (permission === "denied") {
          notify.warning("Oznámení jsou blokována. Povolte je prosím v nastavení prohlížeče, abyste mohli dostávat aktualizace, nebo vypněte PUSH oznámení v nastavení.");
          return;
        }
        if (permission !== "granted") {
          notify.error("Nepovedlo se povolit notifikace");
          return;
        }
      }

      await navigator.serviceWorker.register("/sw.js", { scope: "/" });
      const sw = await navigator.serviceWorker.ready;
      let subscription = await sw.pushManager.getSubscription();

      if (!subscription || (subscription.expirationTime && subscription.expirationTime < Date.now())) {
        subscription = await sw.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: this.urlBase64ToUint8Array(process.env.REACT_APP_VAPID_PUBLIC_KEY),
        });
      }

      await restGate.sendRequest({
        method: "POST",
        endpoint: "/push_subscription/subscribe",
        body: subscription,
      });
      if (remember) {
        localStorage.setItem("push_notifications_set", "1");
      }
    } catch (error) {
      console.error(error);
      await this.unregister();
    }
  }

  static async unregister(remember = true): Promise<void> {
    try {
      const serviceWorkers = await navigator.serviceWorker.getRegistrations();
      for (const sw of serviceWorkers) {
        const sub = await sw.pushManager.getSubscription();
        if (sub) {
          await restGate.sendRequest({
            method: "POST",
            endpoint: "/push_subscription/unsubscribe",
            body: sub,
          });
          await sub.unsubscribe();
        }
        await sw.unregister();
        console.log(`Service Worker deleted`);
      }
      if (remember) {
        localStorage.removeItem("push_notifications_set");
      }
    } catch (error) {
      console.error((error as Error).message);
    }
  }

  static async getAll(): Promise<PushSubscription[]> {
    const response = await restGate.sendRequest<any[]>({ method: "GET", endpoint: "/push_subscription" });
    return response.map((p_s) => ({
      user_id: p_s.user_id,
      user_name: p_s.user_name,
    }));
  }

  static async send({ header, body, receivers }: { header: string; body: string; receivers: number[] }): Promise<any> {
    return restGate.sendRequest({
      method: "POST",
      endpoint: "/push_subscription/multicast",
      body: { header, body, receivers },
    });
  }
}

// ─────────────────────────────────────────────────────────────
// Device
// ─────────────────────────────────────────────────────────────

class Device extends Helper {
  static async ensureCreated(): Promise<void> {
    const device_id = localStorage.getItem("device_id");
    const response = await restGate.sendRequest<{ id: string }>({
      method: "POST",
      endpoint: "/device/ensure_created",
      ...(device_id ? { body: { device_id } } : {}),
      auth: false,
    });
    console.log(response);
    localStorage.setItem("device_id", response.id);
  }

  static async pairUser(): Promise<any> {
    return restGate.sendRequest({
      method: "POST",
      endpoint: "/device/pair_user",
      body: { device_id: localStorage.getItem("device_id") },
    });
  }
}

// ─────────────────────────────────────────────────────────────
// Beacon
// ─────────────────────────────────────────────────────────────

class BeaconEventType {
  static ON_PAGE_CLOSE = "ON_PAGE_CLOSE";
  static ON_PAGE_OPEN = "ON_PAGE_OPEN";
  static ON_REGISTER = "ON_REGISTER";
  static ON_LOGIN = "ON_LOGIN";
  static ON_LOGOUT = "ON_LOGOUT";
  static ON_PAGE_CHANGE = "ON_PAGE_CHANGE";
  static ON_QR_SCAN = "ON_QR_SCAN";
  static ON_ACTION_BUTTON_CLICKED = "ON_ACTION_BUTTON_CLICKED";
}

interface BeaconEvent {
  event: string;
  metadata: Record<string, unknown>;
}

class Beacon {
  static queue: BeaconEvent[] = [];
  static deviceId: string | null = null;
  static inUse = false;
  static flushLen = 1;

  static init(): void {
    Beacon.queue = [];
    Beacon.deviceId = localStorage.getItem("device_id");
    Beacon.inUse = !!Beacon.deviceId;
  }

  static addEvent(eventType: string, metadata: Record<string, unknown> = {}): void {
    if (!Beacon.inUse) return;

    metadata.timestamp = Math.round(Date.now() / 1000);
    Beacon.queue.push({ event: eventType, metadata });

    if (Beacon.queue.length >= Beacon.flushLen) {
      Beacon.flushEvents();
    }
  }

  // this method is called as fire&forget
  static async flushEvents(): Promise<void> {
    if (!Beacon.inUse) return;
    if (Beacon.queue.length === 0) return;

    // Snapshot the current events and clear the queue immediately to avoid
    // race conditions if new events come in during the network request.
    const eventsToSend = [...Beacon.queue];
    Beacon.queue = [];

    try {
      await restGate.sendBeacon({ device_id: Beacon.deviceId, event_list: eventsToSend });
    } catch (error) {
      console.error("Beacon flush failed:", error);
    }
  }

  static async getStats(statisticsName: string): Promise<any> {
    return restGate.sendRequest({
      method: "GET",
      endpoint: "/beacon/get_stats",
      params: {
        statistics_name: statisticsName
      },
    });
  }

  static async getConversionStats(weeks_back: number) {
    return restGate.sendRequest({
      method: "GET",
      endpoint: "/beacon/get_stats/conversion",
      params: {
        weeks_back
      },
    });
  }
}

export {
  User,
  Category,
  Bonus,
  Order,
  Investment,
  Tier,
  PromoCodeObj,
  BonusHistoryObj,
  InvestmentCategory,
  Referenced,
  Transaction,
  Company,
  Game,
  BonusType,
  Generation,
  BonusProgress,
  ReferralCode,
  FaqObj,
  CustomBonus,
  Statistics,
  ConstantsManager,
  Risk,
  SpecialMark,
  TransStatus,
  TransType,
  PushNotificationManager,
  BeaconEventType,
  Beacon,
  Device,
  ScreeniaSession
};
